/**
 * "Add to home screen": Chrome/Edge (Android, desktop) fire `beforeinstallprompt`, which we
 * keep to show the native dialog from our own button. iOS has no such event, so iOS users
 * who are not running the installed app get a short how-to instead.
 */
import { computed, signal } from '@preact/signals';
import { isIos, isStandalone } from './platform';
import type { InstallMode, InstallPrompt } from './types';

/** Chromium's install prompt event (not in the DOM typings). */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isInstallPromptEvent(event: Event): event is BeforeInstallPromptEvent {
  return 'prompt' in event && typeof event.prompt === 'function';
}

export function createInstallPrompt(win: Window = window): InstallPrompt {
  const standalone = signal(isStandalone(win));
  const deferred = signal<BeforeInstallPromptEvent | null>(null);
  const ios = isIos(win.navigator);

  win.addEventListener('beforeinstallprompt', (event) => {
    if (!isInstallPromptEvent(event)) return;
    // Keep the event: the browser's mini-infobar is replaced by our button.
    event.preventDefault();
    deferred.value = event;
  });
  win.addEventListener('appinstalled', () => {
    deferred.value = null;
    standalone.value = true;
  });
  win.matchMedia('(display-mode: standalone)').addEventListener('change', () => {
    standalone.value = isStandalone(win);
  });

  const mode = computed<InstallMode>(() => {
    if (standalone.value) return 'none';
    if (deferred.value) return 'prompt';
    return ios ? 'ios-guide' : 'none';
  });

  return {
    mode,
    async prompt() {
      const event = deferred.peek();
      if (!event) return false;
      // An install prompt event can be used only once.
      deferred.value = null;
      await event.prompt();
      const choice = await event.userChoice;
      return choice.outcome === 'accepted';
    },
  };
}
